import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  StickyNote, Pin, PinOff, Trash2, Edit2, Save, X,
  Loader2, MessageSquare, Info, AlertTriangle, Search, Scale, Receipt,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'info',         label: 'Info',         color: 'slate',   Icon: Info },
  { value: 'important',    label: 'Important',    color: 'amber',   Icon: AlertTriangle },
  { value: 'follow_up',    label: 'Suivi',        color: 'blue',    Icon: Search },
  { value: 'litige',       label: 'Litige',       color: 'red',     Icon: Scale },
  { value: 'facturation',  label: 'Facturation',  color: 'emerald', Icon: Receipt },
];

const COLOR_MAP = {
  slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   dot: 'bg-slate-400' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    dot: 'bg-blue-500' },
  red:     { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     dot: 'bg-red-500' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const fmtRelative = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    const diffMs = Date.now() - date.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "à l'instant";
    if (sec < 3600) return `il y a ${Math.floor(sec / 60)} min`;
    if (sec < 86400) return `il y a ${Math.floor(sec / 3600)} h`;
    if (sec < 7 * 86400) return `il y a ${Math.floor(sec / 86400)} j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
};

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const extractErr = (e, fb = 'Échec') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return e?.message || fb;
};

const NoteCard = ({ note, currentUserId, currentUserRole, onEdit, onDelete, onPin }) => {
  const cat = CATEGORIES.find(c => c.value === note.category) || CATEGORIES[0];
  const colors = COLOR_MAP[cat.color];
  const Icon = cat.Icon;
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [editCategory, setEditCategory] = useState(note.category);
  const [saving, setSaving] = useState(false);

  const isAuthor = note.createdBy === currentUserId;
  const isAdmin = ['admin', 'super_admin'].includes(currentUserRole);
  const canDelete = isAuthor || isAdmin;
  const canPin = isAdmin;

  // 15min edit window
  const editWindowOk = (() => {
    if (!isAuthor) return false;
    try {
      const created = new Date(note.createdAt).getTime();
      return Date.now() - created < 15 * 60 * 1000;
    } catch { return false; }
  })();

  const submitEdit = async () => {
    setSaving(true);
    try {
      await onEdit(note.id, { content: editContent, category: editCategory });
      setEditing(false);
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Card className={`${colors.bg} ${colors.border} ${note.isPinned ? 'ring-2 ring-emerald-300' : ''}`}
          data-testid={`note-card-${note.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-semibold">
              {initials(note.createdByName)}
            </div>
            <div className="text-sm">
              <span className="font-semibold text-slate-800">{note.createdByName || 'Utilisateur'}</span>
              <span className="text-slate-400 mx-1.5">·</span>
              <span className="text-xs text-slate-500" title={note.createdAt}>
                {fmtRelative(note.createdAt)}
              </span>
              {note.editedAt && <span className="text-[10px] text-slate-400 italic ml-1">(édité)</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Badge className={`${colors.bg} ${colors.text} ${colors.border} text-[10px] font-medium border`}>
              <Icon className="w-3 h-3 mr-1" />
              {cat.label}
            </Badge>
            {note.isPinned && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]"
                     data-testid={`note-pinned-${note.id}`}>
                <Pin className="w-3 h-3 mr-1" /> Épinglée
              </Badge>
            )}
          </div>
        </div>

        {editing ? (
          <div className="space-y-2">
            <Select value={editCategory} onValueChange={setEditCategory}>
              <SelectTrigger className="h-8 text-xs w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={3}
              className="text-sm bg-white"
              maxLength={4000}
              data-testid={`note-edit-textarea-${note.id}`}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditContent(note.content); }}>
                <X className="w-3 h-3 mr-1" /> Annuler
              </Button>
              <Button size="sm" onClick={submitEdit} disabled={saving || !editContent.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`note-save-edit-${note.id}`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">
            {note.content}
          </div>
        )}

        {/* Actions */}
        {!editing && (canDelete || canPin || editWindowOk) && (
          <div className="mt-3 pt-2 border-t border-slate-200/60 flex items-center justify-end gap-1">
            {canPin && (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-600"
                      onClick={() => onPin(note.id, !note.isPinned)}
                      data-testid={`note-pin-btn-${note.id}`}>
                {note.isPinned ? <PinOff className="w-3 h-3 mr-1" /> : <Pin className="w-3 h-3 mr-1" />}
                {note.isPinned ? 'Désépingler' : 'Épingler'}
              </Button>
            )}
            {editWindowOk && (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-600"
                      onClick={() => setEditing(true)}
                      data-testid={`note-edit-btn-${note.id}`}>
                <Edit2 className="w-3 h-3 mr-1" /> Modifier
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(note.id)}
                      data-testid={`note-delete-btn-${note.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const NoteForm = ({ onSubmit }) => {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('info');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(content.trim(), category);
      setContent('');
      setCategory('info');
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-600" />
          <h4 className="text-sm font-semibold text-slate-800">Nouvelle note interne</h4>
        </div>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, 4000))}
          rows={3}
          placeholder="Saisissez votre note interne (visible par le staff uniquement)…"
          className="text-sm resize-none"
          data-testid="note-form-content"
        />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 text-sm w-[180px]" data-testid="note-form-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => {
                const colors = COLOR_MAP[c.color];
                const Icon = c.Icon;
                return (
                  <SelectItem key={c.value} value={c.value}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                      <Icon className="w-3 h-3" /> {c.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400">{content.length} / 4000</span>
            <Button size="sm" onClick={submit} disabled={submitting || !content.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="note-form-submit">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              + Publier la note
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Onglet Notes internes pour opérations & contrats (PM Spec 05/05/2026).
 *
 * Props :
 *   - entityType : 'operation' | 'contract'
 *   - entityId : ID de l'entité parent
 *   - currentUser : { id, role, firstName, lastName } (passe-plat depuis le contexte parent)
 *   - onCountChange : callback (number) appelé après refresh, utile pour afficher le badge sur l'onglet
 */
const InternalNotesTab = ({ entityType, entityId, currentUser, onCountChange }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = () => 'cookie-auth';
  const ax = useCallback(() => axios.create({
    baseURL: API
  }), []);

  const fetchNotes = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const r = await ax().get(`/internal-notes?entityType=${entityType}&entityId=${entityId}`);
      const list = r.data?.notes || [];
      setNotes(list);
      onCountChange?.(list.length);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
      }
    } finally { setLoading(false); }
  }, [ax, entityType, entityId, onCountChange]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleCreate = async (content, category) => {
    await ax().post('/internal-notes', { entityType, entityId, content, category });
    await fetchNotes();
  };
  const handleEdit = async (noteId, payload) => {
    await ax().patch(`/internal-notes/${noteId}`, payload);
    await fetchNotes();
  };
  const handleDelete = async (noteId) => {
    if (!window.confirm('Supprimer cette note ? (cette action est tracée)')) return;
    try {
      await ax().delete(`/internal-notes/${noteId}`);
      await fetchNotes();
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    }
  };
  const handlePin = async (noteId, isPinned) => {
    try {
      await ax().patch(`/internal-notes/${noteId}/pin`, { isPinned });
      await fetchNotes();
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    }
  };

  const pinnedNotes = notes.filter(n => n.isPinned);
  const otherNotes = notes.filter(n => !n.isPinned);

  return (
    <div className="space-y-4" data-testid={`internal-notes-tab-${entityType}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-emerald-600" />
          <div>
            <h3 className="text-base font-semibold text-slate-800">Notes internes</h3>
            <p className="text-xs text-slate-500">
              Visibles uniquement par le staff. Le client ne voit jamais ces notes.
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">
          {notes.length} note{notes.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      <NoteForm onSubmit={handleCreate} />

      {loading ? (
        <div className="text-center py-6 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
          <StickyNote className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Aucune note interne pour le moment.</p>
        </div>
      ) : (
        <>
          {pinnedNotes.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                <Pin className="w-3 h-3" /> Épinglée
              </div>
              {pinnedNotes.map(note => (
                <NoteCard key={note.id} note={note}
                          currentUserId={currentUser?.id || currentUser?.user_id}
                          currentUserRole={currentUser?.role}
                          onEdit={handleEdit} onDelete={handleDelete} onPin={handlePin} />
              ))}
            </div>
          )}
          {otherNotes.length > 0 && (
            <div className="space-y-3">
              {pinnedNotes.length > 0 && (
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">
                  Toutes les notes
                </div>
              )}
              {otherNotes.map(note => (
                <NoteCard key={note.id} note={note}
                          currentUserId={currentUser?.id || currentUser?.user_id}
                          currentUserRole={currentUser?.role}
                          onEdit={handleEdit} onDelete={handleDelete} onPin={handlePin} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InternalNotesTab;
